import type { TextRun } from "@bezpart-board/shared";
import type { SceneObject } from "./SceneHost";

/**
 * Оформление части текста: жирный, курсив, подчёркивание, зачёркивание,
 * цвет и размер — для куска, а не для всей надписи целиком.
 *
 * Хранение: объект как и раньше держит весь текст в `text` (по нему идут
 * измерение размера, поиск, копирование), а `runs` — тот же текст, разбитый
 * на куски с оформлением. Если оформление у всего текста одинаковое, `runs`
 * не хранится вовсе: старые надписи и надписи из старых версий работают как
 * работали.
 */

export type { TextRun };

const MAX_RUNS = 400;

/**
 * Ссылка, которую можно хранить и открывать: только http и https.
 * «www.пример.рф» без протокола дополняем до https — так пишут чаще всего.
 */
export function safeLinkHref(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let value = raw.trim();
  if (!value) return null;
  if (/^www\./i.test(value)) value = `https://${value}`;
  else if (!/^[a-z][a-z0-9+.-]*:/i.test(value) && /^[^\s/]+\.[^\s/]{2,}/.test(value)) {
    // «example.com/page» — тоже адрес, просто без протокола.
    value = `https://${value}`;
  }
  try {
    const u = new URL(value);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    const href = u.href;
    return href.length <= 2048 ? href : null;
  } catch {
    return null;
  }
}

export function runsPlainText(runs: TextRun[] | null | undefined): string {
  if (!runs || !runs.length) return "";
  return runs.map((r) => r.text).join("");
}

/**
 * Есть ли в кусках собственное оформление.
 *
 * Снятый признак (`bold: false` у куска жирной надписи) — тоже оформление:
 * без этого «сделать часть не жирной» было нечем записать, и надпись
 * возвращалась к общему виду.
 */
export function runsHaveMarks(runs: TextRun[] | null | undefined): boolean {
  if (!runs) return false;
  return runs.some(
    (r) =>
      r.bold !== undefined ||
      r.italic !== undefined ||
      r.underline !== undefined ||
      r.strike !== undefined ||
      Boolean(r.color) ||
      Boolean(r.fontSize) ||
      Boolean(r.highlight) ||
      Boolean(r.href)
  );
}

/**
 * Куски без пробелов и пустых строк по краям — так же, как текст надписи
 * обрезается при сохранении.
 */
export function runsTrimEdges(runs: TextRun[]): TextRun[] {
  const out = runs.map((r) => ({ ...r }));
  while (out.length) {
    const first = out[0]!;
    const t = first.text.replace(/^\s+/, "");
    if (t) {
      first.text = t;
      break;
    }
    out.shift();
  }
  while (out.length) {
    const last = out[out.length - 1]!;
    const t = last.text.replace(/\s+$/, "");
    if (t) {
      last.text = t;
      break;
    }
    out.pop();
  }
  return out;
}

export function objectRuns(obj: SceneObject | null | undefined): TextRun[] | null {
  const raw = (obj as { runs?: unknown } | null | undefined)?.runs;
  if (!Array.isArray(raw) || !raw.length) return null;
  const out: TextRun[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    if (typeof r.text !== "string") continue;
    const run: TextRun = { text: r.text };
    if (typeof r.bold === "boolean") run.bold = r.bold;
    if (typeof r.italic === "boolean") run.italic = r.italic;
    if (typeof r.underline === "boolean") run.underline = r.underline;
    if (typeof r.strike === "boolean") run.strike = r.strike;
    if (typeof r.color === "string" && r.color) run.color = r.color;
    if (typeof r.highlight === "string" && r.highlight) run.highlight = r.highlight;
    const href = safeLinkHref(r.href);
    if (href) run.href = href;
    if (typeof r.fontSize === "number" && Number.isFinite(r.fontSize)) {
      run.fontSize = r.fontSize;
    }
    out.push(run);
  }
  if (!out.length) return null;
  // Старые надписи: при сохранении текст обрезали по краям, а куски — нет.
  // Доска рисовала их с пустыми строками сверху, а рамку мерила по тексту
  // без них — текст «выпадал» из рамки. Если куски отличаются от текста
  // только краями, показываем их обрезанными.
  const text = (obj as { text?: unknown } | null | undefined)?.text;
  if (typeof text === "string" && runsPlainText(out) !== text) {
    const trimmed = runsTrimEdges(out);
    if (runsPlainText(trimmed) === text) return trimmed.length ? trimmed : null;
  }
  return out;
}

function sameMarks(a: TextRun, b: TextRun): boolean {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.strike === b.strike &&
    (a.color ?? "") === (b.color ?? "") &&
    (a.highlight ?? "") === (b.highlight ?? "") &&
    (a.href ?? "") === (b.href ?? "") &&
    (a.fontSize ?? 0) === (b.fontSize ?? 0)
  );
}

/** Склеивает соседние куски с одинаковым оформлением и режет пустые. */
export function normalizeRuns(runs: TextRun[]): TextRun[] {
  const out: TextRun[] = [];
  for (const run of runs) {
    if (!run.text) continue;
    const last = out[out.length - 1];
    if (last && sameMarks(last, run)) {
      last.text += run.text;
      continue;
    }
    out.push({ ...run });
  }
  if (out.length <= MAX_RUNS) return out;
  // Слишком дробное оформление: лишнее склеиваем в конец, текст не теряем.
  const head = out.slice(0, MAX_RUNS - 1);
  const tailText = out
    .slice(MAX_RUNS - 1)
    .map((r) => r.text)
    .join("");
  head.push({ text: tailText });
  return head;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

function runStyleCss(run: TextRun, baseFontSize?: number): string {
  const parts: string[] = [];
  // Снятый признак пишем явно (400 / normal / none): надпись целиком может быть
  // жирной или курсивной, и кусок обязан её перебить, а не просто «промолчать».
  if (run.bold === true) parts.push("font-weight:700");
  else if (run.bold === false) parts.push("font-weight:400");
  if (run.italic === true) parts.push("font-style:italic");
  else if (run.italic === false) parts.push("font-style:normal");
  const deco: string[] = [];
  if (run.underline) deco.push("underline");
  if (run.strike) deco.push("line-through");
  if (deco.length) parts.push(`text-decoration:${deco.join(" ")}`);
  else if (run.underline === false || run.strike === false) {
    parts.push("text-decoration:none");
  }
  if (run.color) parts.push(`color:${run.color}`);
  // Маркер — заливка под буквами, как у текстового маркера в тетради.
  if (run.highlight) parts.push(`background-color:${run.highlight}`);
  if (run.fontSize) {
    // В поле набора — в долях размера надписи: поле нарисовано в масштабе
    // экрана, и точки доски там были бы не того размера.
    if (baseFontSize && baseFontSize > 0) {
      const em = Math.round((run.fontSize / baseFontSize) * 10000) / 10000;
      parts.push(`font-size:${em}em`);
    } else {
      parts.push(`font-size:${run.fontSize}px`);
    }
  }
  return parts.join(";");
}

/** HTML для показа и для поля набора. Переводы строк — как в тексте. */
/**
 * HTML кусков. Без baseFontSize — размеры в точках доски (для показа на
 * доске и для измерения). С ним — в долях размера надписи, для поля
 * набора, которое нарисовано в масштабе экрана.
 */
export function runsToHtml(runs: TextRun[] | null | undefined, baseFontSize?: number): string {
  if (!runs || !runs.length) return "";
  return runs
    .map((run) => {
      const text = escapeHtml(run.text).replace(/\n/g, "<br>");
      const css = runStyleCss(run, baseFontSize);
      // Ссылка — настоящая <a>: так её видно и в поле набора, и на доске,
      // а доска по ней находит, какой адрес открыть по щелчку.
      const href = run.href ? safeLinkHref(run.href) : null;
      if (href) {
        const style = css ? ` style="${css}"` : "";
        return `<a href="${escapeAttr(href)}" data-wb-link="" target="_blank" rel="noopener noreferrer"${style}>${text}</a>`;
      }
      return css ? `<span style="${css}">${text}</span>` : text;
    })
    .join("");
}

type Marks = Omit<TextRun, "text">;

function marksFromElement(el: HTMLElement, inherited: Marks, baseFontSize?: number): Marks {
  const next: Marks = { ...inherited };
  const tag = el.tagName;
  if (tag === "B" || tag === "STRONG") next.bold = true;
  if (tag === "I" || tag === "EM") next.italic = true;
  if (tag === "U") next.underline = true;
  if (tag === "S" || tag === "STRIKE" || tag === "DEL") next.strike = true;
  if (tag === "A") {
    const href = safeLinkHref(el.getAttribute("href"));
    if (href) next.href = href;
  }

  const style = el.style;
  const weight = style.fontWeight;
  if (weight) next.bold = weight === "bold" || Number(weight) >= 600;
  if (style.fontStyle) next.italic = style.fontStyle === "italic";
  const deco = `${style.textDecoration} ${style.textDecorationLine}`.trim();
  if (deco) {
    if (/underline/.test(deco)) next.underline = true;
    if (/line-through/.test(deco)) next.strike = true;
    if (/\bnone\b/.test(deco) && !/underline|line-through/.test(deco)) {
      next.underline = false;
      next.strike = false;
    }
  }
  if (style.color) next.color = style.color;
  // Маркер. Прозрачная заливка — это «маркер снят», а не цвет.
  const bg = style.backgroundColor;
  if (bg === "transparent" || /^rgba\([^)]*,\s*0\)$/.test(bg)) delete next.highlight;
  else if (bg) next.highlight = bg;
  // Размер в долях (поле набора) — от размера родителя, а он от размера
  // надписи. Иначе — точки доски.
  if (/em$/.test(style.fontSize) && !/rem$/.test(style.fontSize) && baseFontSize) {
    const em = parseFloat(style.fontSize);
    const parent = inherited.fontSize ?? baseFontSize;
    if (Number.isFinite(em) && em > 0) next.fontSize = Math.round(em * parent * 100) / 100;
    return next;
  }
  const size = parseFloat(style.fontSize);
  if (Number.isFinite(size) && size > 0) next.fontSize = Math.round(size * 100) / 100;
  return next;
}

function cleanMarks(marks: Marks): Marks {
  const out: Marks = {};
  if (marks.bold !== undefined) out.bold = marks.bold;
  if (marks.italic !== undefined) out.italic = marks.italic;
  if (marks.underline !== undefined) out.underline = marks.underline;
  if (marks.strike !== undefined) out.strike = marks.strike;
  if (marks.color) out.color = marks.color;
  if (marks.highlight) out.highlight = marks.highlight;
  if (marks.fontSize) out.fontSize = marks.fontSize;
  if (marks.href) out.href = marks.href;
  return out;
}

const BLOCK_TAGS = new Set(["DIV", "P", "LI", "TR"]);

/**
 * Разбирает содержимое поля набора в куски с оформлением.
 * Понимает и теги (b/i/u/s), и inline-стили — браузеры при форматировании
 * выделения ставят то одно, то другое.
 */
export function htmlToRuns(root: HTMLElement): TextRun[] {
  // Поле набора помечено размером надписи — размеры кусков в нём в долях.
  const baseAttr = Number(root.dataset?.wbBaseFont);
  const baseFontSize = Number.isFinite(baseAttr) && baseAttr > 0 ? baseAttr : undefined;
  const runs: TextRun[] = [];
  let pendingNewline = false;

  const pushText = (text: string, marks: Marks) => {
    if (!text) return;
    if (pendingNewline) {
      runs.push({ text: "\n", ...cleanMarks({}) });
      pendingNewline = false;
    }
    runs.push({ text, ...cleanMarks(marks) });
  };

  const walk = (node: Node, marks: Marks, topLevel: boolean) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        pushText((child.textContent ?? "").replace(/\r\n/g, "\n"), marks);
        continue;
      }
      if (!(child instanceof HTMLElement)) continue;
      if (child.tagName === "BR") {
        // Хвостовой перенос поле ставит только затем, чтобы показать пустую
        // последнюю строку, — в текст он не входит.
        if (child.hasAttribute("data-wb-tail")) continue;
        pushText("\n", marks);
        continue;
      }
      const isBlock = BLOCK_TAGS.has(child.tagName);
      if (isBlock && !topLevel) pushText("\n", marks);
      else if (isBlock && runs.length) pendingNewline = true;
      walk(child, marksFromElement(child, marks, baseFontSize), false);
    }
  };

  walk(root, {}, true);
  return normalizeRuns(runs);
}

/**
 * Что писать в объект: текст без оформления и куски (или их удаление).
 * Куски не храним, когда оформление везде одинаковое — сцена не пухнет,
 * а объект остаётся таким же, как у старых надписей.
 */
export function runsPatch(runs: TextRun[]): { text: string; runs?: TextRun[] | null } {
  const normalized = normalizeRuns(runs);
  const text = runsPlainText(normalized);
  if (!runsHaveMarks(normalized)) return { text, runs: null };
  return { text, runs: normalized };
}

/** Одинаковое ли оформление у всех кусков — тогда его можно снять в style. */
export function runsUniformMarks(runs: TextRun[] | null | undefined): Marks | null {
  if (!runs || !runs.length) return null;
  const first = cleanMarks(runs[0]!);
  for (const run of runs) {
    if (!sameMarks({ text: "", ...first }, run)) return null;
  }
  return first;
}


/**
 * Заменить [start, end) обычного текста на text, не теряя оформления.
 * Вставленное берёт оформление символа перед местом вставки (как в любом
 * редакторе: печатаешь после жирного — пишешь жирным), а в самом начале —
 * первого символа.
 */
export function runsReplace(runs: TextRun[], start: number, end: number, text: string): TextRun[] {
  const out: TextRun[] = [];
  let pos = 0;
  let inserted = false;
  let carrier: Marks | null = null;
  const marksOf = (r: TextRun): Marks => {
    const { text: _t, ...m } = r;
    return m;
  };

  for (const run of runs) {
    const runStart = pos;
    const runEnd = pos + run.text.length;
    pos = runEnd;
    if (runEnd <= start) {
      out.push({ ...run });
      if (runEnd === start) carrier = marksOf(run);
      continue;
    }
    if (runStart >= end) {
      if (!inserted) {
        out.push({ text, ...(carrier ?? marksOf(run)) });
        inserted = true;
      }
      out.push({ ...run });
      continue;
    }
    // Кусок задет заменой: оставляем голову и хвост, середину меняем.
    const head = run.text.slice(0, Math.max(0, start - runStart));
    const tail = run.text.slice(Math.max(0, end - runStart));
    if (head) {
      out.push({ ...run, text: head });
      carrier = marksOf(run);
    }
    if (!inserted) {
      out.push({ text, ...(carrier ?? marksOf(run)) });
      inserted = true;
    }
    if (tail) out.push({ ...run, text: tail });
  }
  if (!inserted) out.push({ text, ...(carrier ?? {}) });
  return normalizeRuns(out);
}

/** Поставить или снять маркер на [start, end). */
export function runsSetHighlight(
  runs: TextRun[],
  start: number,
  end: number,
  highlight: string | null
): TextRun[] {
  return runsSetMark(runs, start, end, (mid) => {
    if (highlight) mid.highlight = highlight;
    else delete mid.highlight;
  });
}

/** Поставить или снять ссылку на [start, end). */
export function runsSetHref(
  runs: TextRun[],
  start: number,
  end: number,
  href: string | null
): TextRun[] {
  const safe = href ? safeLinkHref(href) : null;
  return runsSetMark(runs, start, end, (mid) => {
    if (safe) mid.href = safe;
    else delete mid.href;
  });
}

/**
 * Ссылка под курсором: её границы и адрес. Нужна, чтобы поправить или
 * снять ссылку, просто поставив курсор в неё, без выделения.
 */
export function linkRangeAt(
  runs: TextRun[],
  offset: number
): { start: number; end: number; href: string } | null {
  let pos = 0;
  const spans: { start: number; end: number; href?: string }[] = [];
  for (const run of runs) {
    spans.push({ start: pos, end: pos + run.text.length, href: run.href });
    pos += run.text.length;
  }
  const hit = spans.findIndex(
    (s) => s.href && offset >= s.start && offset <= s.end
  );
  if (hit < 0) return null;
  const href = spans[hit]!.href!;
  let a = hit;
  let b = hit;
  while (a > 0 && spans[a - 1]!.href === href) a--;
  while (b < spans.length - 1 && spans[b + 1]!.href === href) b++;
  return { start: spans[a]!.start, end: spans[b]!.end, href };
}

const URL_IN_TEXT = /(?:https?:\/\/|www\.)[^\s<>"«»]+/gi;

/**
 * Адреса в тексте становятся ссылками, как в Miro: вставил или набрал
 * адрес — по нему можно перейти. Куски, где ссылка уже есть, не трогаем.
 */
export function runsAutolink(runs: TextRun[]): TextRun[] {
  const text = runsPlainText(runs);
  let out = runs;
  URL_IN_TEXT.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = URL_IN_TEXT.exec(text))) {
    // Точка или скобка в конце предложения — не часть адреса.
    const raw = m[0].replace(/[.,;:!?)\]}'"»]+$/, "");
    if (!raw) continue;
    const start = m.index;
    const end = start + raw.length;
    let pos = 0;
    let linked = false;
    for (const run of out) {
      const rs = pos;
      pos += run.text.length;
      if (pos > start && rs < end && run.href) linked = true;
    }
    if (linked) continue;
    const href = safeLinkHref(raw);
    if (href) out = runsSetHref(out, start, end, href);
  }
  return out;
}

/** Применить правку оформления к каждому кусочку на [start, end). */
function runsSetMark(
  runs: TextRun[],
  start: number,
  end: number,
  apply: (mid: TextRun) => void
): TextRun[] {
  const out: TextRun[] = [];
  let pos = 0;
  for (const run of runs) {
    const runStart = pos;
    const runEnd = pos + run.text.length;
    pos = runEnd;
    if (runEnd <= start || runStart >= end) {
      out.push({ ...run });
      continue;
    }
    const a = Math.max(start, runStart) - runStart;
    const b = Math.min(end, runEnd) - runStart;
    if (a > 0) out.push({ ...run, text: run.text.slice(0, a) });
    const mid: TextRun = { ...run, text: run.text.slice(a, b) };
    apply(mid);
    out.push(mid);
    if (b < run.text.length) out.push({ ...run, text: run.text.slice(b) });
  }
  return normalizeRuns(out);
}

/**
 * Куски без собственного размера шрифта. Когда размер меняют у всей
 * надписи, он должен лечь на весь текст: кусок со своим размером (часто
 * приехавший вставкой со страницы) иначе оставался прежним, а рамка
 * пересчитывалась вхолостую.
 */
export function runsWithoutFontSize(runs: TextRun[]): TextRun[] {
  return normalizeRuns(
    runs.map((r) => {
      const { fontSize: _size, ...rest } = r;
      return rest;
    })
  );
}

/** Границы размера куска — как у сервера (sceneSchema: 4–400). */
const RUN_FONT_MIN = 4;
const RUN_FONT_MAX = 400;

/**
 * Собственные размеры кусков в ratio раз. Нужен при растягивании надписи
 * за угол: общий размер масштабировался, а куски со своим размером
 * (вставка со страницы, выделенное слово крупнее) оставались прежними —
 * рамка росла, а буквы нет.
 */
export function runsScaleFontSize(runs: TextRun[], ratio: number): TextRun[] {
  if (!Number.isFinite(ratio) || ratio <= 0) return runs.map((r) => ({ ...r }));
  return runs.map((r) => {
    if (!r.fontSize) return { ...r };
    const next = Math.round(r.fontSize * ratio * 10) / 10;
    return { ...r, fontSize: Math.min(RUN_FONT_MAX, Math.max(RUN_FONT_MIN, next)) };
  });
}
